import React, { createContext } from 'react'
import { BaseContextState, BaseContextProvider } from '../BaseContext'
import { SearchRequest, MappingCharacteristicSaveRequest } from '../../services/api/interfaces/ApiRequest'
import IMappingCharacteristic from 'interfaces/IMappingCharacteristic'
import { mappingCharacteristicList, mappingCharacteristicSave } from 'services/api'

interface MappingCharacteristicContextState extends BaseContextState {
  mappingCharacteristics: Array<IMappingCharacteristic> | null,
  mappingCharacteristic: IMappingCharacteristic | null
}

interface MappingCharacteristicContextData extends MappingCharacteristicContextState {
  resetErrors: Function,
  requestMappingCharacteristicList: (args: SearchRequest) => Promise<any>,
  requestMappingCharacteristicFind: (args: SearchRequest) => Promise<any>,
  requestMappingCharacteristicSave: (args: MappingCharacteristicSaveRequest) => void,
}

// Create context
const { Provider, Consumer } = createContext({} as MappingCharacteristicContextData)

// Export consumer
export const MappingCharacteristicConsumer = Consumer

export class MappingCharacteristicProvider extends BaseContextProvider {
  state: MappingCharacteristicContextState = {
    mappingCharacteristics: null,
    mappingCharacteristic: null
  }

  /**
   * Make api request for mapping characteristic list
   * 
   * @param {SearchRequest} payload 
   */
  requestMappingCharacteristicList = (payload: SearchRequest) => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null
      }, async () => {
        const response = await mappingCharacteristicList(payload)
  
        // Process our response
        this.processResponse(response, { 
          paginate: {
            destination: 'mappingCharacteristics'
          },
        })

        if (this.state.success) {
          resolve(true)
        } else {
          reject(this.state.error)
        }
      })
    })
  }

  /**
   * Make api request for mapping characteristic finder
   * 
   * @param {SearchRequest} payload 
   */
  requestMappingCharacteristicFind = (payload: SearchRequest) => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null,
        mappingCharacteristic: null
      }, async () => {
        payload.search = {
          ...payload.search,
          characteristic: {
            with: true
          },
          shelter: {
            with: {
              _fields: ["id", "uuid", "name"],
            }
          }
        }
        payload.options = {
          ...payload.options,
          _single: true
        }
  
        const response = await mappingCharacteristicList(payload)
  
        // Process our response
        this.processResponse(response, { 
          mappings: {
            destination: 'mappingCharacteristic'
          },
        })

        if (this.state.success) {
          resolve(true)
        } else {
          reject(this.state.error)
        }
      })
    })
  }

  /**
   * Make api request for mapping characteristic save
   * 
   * @param {MappingCharacteristicSaveRequest} payload 
   */
  requestMappingCharacteristicsSave = (payload: MappingCharacteristicSaveRequest) => {
    // No need to null `mappingCharacteristic` on save: the form stays mounted (its render
    // no longer depends on this value) and re-fetches fresh whenever the modal reopens.
    return new Promise<void>((resolve, reject) => this.setState({
      fetching: true,
      error: null,
      // mappingCharacteristic: null
    }, async () => {
      try {
        const response = await mappingCharacteristicSave(payload)

        // Process our response
        const result = await this.processResponse(response, {
          mappings: [
            { origin: 'mapping_characteristic', destination: 'mappingCharacteristic' }
          ]
        })

        // throw if error happend
        if (result.error) {
          return reject(new Error(result.error))
        }
      } finally {
        resolve()
      }
    }))
  }

  render() {
    return (
      <Provider
        value={{
          // States
          ...this.state,

          // Dispatches
          resetErrors: this.resetErrors,
          requestMappingCharacteristicList: this.requestMappingCharacteristicList,
          requestMappingCharacteristicFind: this.requestMappingCharacteristicFind,
          requestMappingCharacteristicSave: this.requestMappingCharacteristicsSave
        }}
      >
        {this.props.children}
      </Provider>
    )
  }
}
